import {
  Box,
  Button,
  HStack,
  Icon,
  Link,
  Select,
  Text,
  Tooltip,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import { FiTrendingDown, FiTrendingUp } from "react-icons/fi";
import type { CampaignIntent } from "common/interfaces/KYDMarketing";
import { INTENT_TYPE_LABELS } from "common/interfaces/CampaignIntent";
import actions from "src/actions";
import { sortIntentsByImportance } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/CampaignIntentsBlock";
import { getIntentPerformanceCategory } from "src/Manager/EventDetail/Nav/Marketing/ManagedCampaigns/components/intentPerformance";

const PAGE_SIZE = 7;
const BULLET = " · ";

/** Human-readable label for entity type (middle column context, e.g. "Ad Set"). */
const ENTITY_TYPE_LABELS: Record<string, string> = {
  campaign: "Campaign",
  adset: "Ad Set",
  ad: "Ad",
  ad_creative: "Ad creative",
};

/**
 * Extract entity name from action label when it's in double quotes (e.g. "Toggle off ad set \"Wild Hearts\"" -> "Wild Hearts").
 * Labels are built as "Toggle off ad set \"Name\"", "Increase budget of ad set \"Name\" by 20%", etc.
 */
function extractEntityNameFromLabel(label: string | undefined): string | null {
  if (!label || typeof label !== "string") return null;
  const match = label.match(/"([^"]+)"/);
  return match ? match[1].trim() || null : null;
}

/** Positive (green) vs negative (red) icon from intent type. */
function getIntentVariant(
  intent: CampaignIntent
): "positive" | "negative" | "neutral" {
  return getIntentPerformanceCategory(intent);
}

/**
 * Entity name only for the first column (e.g. campaign name, ad set name "Wild Hearts").
 * Never show the action text (e.g. "Toggle off ad set X") — only campaign name, or entity name from label quotes, or generic type.
 */
function getEntityName(
  intent: CampaignIntent,
  campaignNames: Record<string, string>
): string {
  const campaignId = intent.campaign_id ?? "";
  if (campaignNames[campaignId]) return campaignNames[campaignId];
  const first = intent.actions?.[0];
  const actionLabel = (first as { label?: string } | undefined)?.label;
  const fromQuotes = extractEntityNameFromLabel(actionLabel);
  if (fromQuotes) return fromQuotes;
  const entityType = (first as { entity_type?: string } | undefined)
    ?.entity_type;
  if (entityType && ENTITY_TYPE_LABELS[entityType])
    return ENTITY_TYPE_LABELS[entityType];
  return "Campaign";
}

/** Middle column: entity type · diagnosis · metric (e.g. "Ad Set · Underperforming · 0.4x ROAS"). Action label stays in the right column. */
function getIntentDescription(intent: CampaignIntent): string {
  const parts: string[] = [];
  const first = intent.actions?.[0];
  const entityType = (first as { entity_type?: string } | undefined)
    ?.entity_type;
  if (entityType && ENTITY_TYPE_LABELS[entityType])
    parts.push(ENTITY_TYPE_LABELS[entityType]);
  const summary =
    intent.explanation?.short_summary ??
    intent.explanation?.summary ??
    intent.reason;
  if (summary) parts.push(summary);
  return parts.join(BULLET) || "—";
}

/** Full explanation for tooltip (same as campaign detail). */
function getFullExplanation(intent: CampaignIntent): string {
  return intent.explanation?.summary ?? intent.reason ?? "";
}

/** Action label for the right column (clickable Apply). */
function getActionLabel(intent: CampaignIntent): string {
  return (
    INTENT_TYPE_LABELS[intent.intent_type as keyof typeof INTENT_TYPE_LABELS] ??
    intent.intent_type.replace(/_/g, " ").toLowerCase()
  );
}

export interface ProactiveInsightsSectionProps {
  intents: CampaignIntent[];
  onRefetch: () => void | Promise<void>;
  /** Optional map of campaign_id -> display name (e.g. campaign or event name). */
  campaignNames?: Record<string, string>;
}

export default function ProactiveInsightsSection({
  intents,
  onRefetch,
  campaignNames = {},
}: ProactiveInsightsSectionProps) {
  const toast = useToast();

  const visible = intents.filter(
    (i) =>
      (i.status as string) !== "DISABLED" && (i.status as string) !== "IGNORED"
  );
  const sorted = sortIntentsByImportance(visible);
  const totalCount = sorted.length;
  const pageCount = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
  const [page, setPage] = useState(1);
  const start = (page - 1) * PAGE_SIZE;
  const pageIntents = sorted.slice(start, start + PAGE_SIZE);

  const [applyingId, setApplyingId] = useState<string | null>(null);
  const [ignoringId, setIgnoringId] = useState<string | null>(null);
  const [applyAllLoading, setApplyAllLoading] = useState(false);
  const [dismissAllLoading, setDismissAllLoading] = useState(false);

  useEffect(() => {
    if (page > pageCount && pageCount >= 1) setPage(pageCount);
  }, [page, pageCount]);

  const handleApply = useCallback(
    async (intent: CampaignIntent) => {
      if (!intent.actionable || !intent.campaign_id) return;
      setApplyingId(intent.id);
      try {
        await actions.ADMIN.batchApplyCampaignIntents([
          {
            campaign_id: intent.campaign_id,
            intent_ids: [intent.id],
          },
        ]);
        toast({
          title: "Intent applied",
          status: "success",
          isClosable: true,
        });
        await onRefetch();
      } catch (err: unknown) {
        toast({
          title: "Failed to apply",
          description: (err as Error)?.message ?? "Could not apply.",
          status: "error",
          isClosable: true,
        });
      } finally {
        setApplyingId(null);
      }
    },
    [toast, onRefetch]
  );

  const handleIgnore = useCallback(
    async (intent: CampaignIntent) => {
      if (!intent.campaign_id) return;
      setIgnoringId(intent.id);
      try {
        await actions.ADMIN.ignoreCampaignIntent(intent.campaign_id, [intent]);
        toast({
          title: "Insight dismissed",
          status: "success",
          isClosable: true,
        });
        await onRefetch();
      } catch (err: unknown) {
        toast({
          title: "Could not dismiss",
          description: (err as Error)?.message ?? "Could not save as ignored.",
          status: "warning",
          isClosable: true,
        });
      } finally {
        setIgnoringId(null);
      }
    },
    [toast, onRefetch]
  );

  const handleApplyAll = useCallback(async () => {
    const actionable = pageIntents.filter((i) => i.actionable && i.campaign_id);
    if (actionable.length === 0) return;
    const byCampaign = new Map<string, string[]>();
    for (const intent of actionable) {
      const cid = intent.campaign_id!;
      if (!byCampaign.has(cid)) byCampaign.set(cid, []);
      byCampaign.get(cid)!.push(intent.id);
    }
    const operations = Array.from(byCampaign.entries()).map(
      ([campaign_id, intent_ids]) => ({ campaign_id, intent_ids })
    );
    setApplyAllLoading(true);
    try {
      await actions.ADMIN.batchApplyCampaignIntents(operations);
      toast({
        title: "Applied",
        description: `${actionable.length} insight(s) applied.`,
        status: "success",
        isClosable: true,
      });
      await onRefetch();
    } catch (err: unknown) {
      toast({
        title: "Failed to apply all",
        description: (err as Error)?.message ?? "Could not apply.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setApplyAllLoading(false);
    }
  }, [pageIntents, toast, onRefetch]);

  const handleDismissAll = useCallback(async () => {
    const withCampaign = pageIntents.filter((i) => i.campaign_id);
    if (withCampaign.length === 0) return;
    const byCampaign = new Map<string, string[]>();
    for (const intent of withCampaign) {
      const cid = intent.campaign_id!;
      if (!byCampaign.has(cid)) byCampaign.set(cid, []);
      byCampaign.get(cid)!.push(intent.id);
    }
    const operations = Array.from(byCampaign.entries()).map(
      ([campaign_id, intent_ids]) => ({ campaign_id, intent_ids })
    );
    setDismissAllLoading(true);
    try {
      await actions.ADMIN.batchIgnoreCampaignIntents(operations);
      toast({
        title: "Dismissed",
        description: `${withCampaign.length} insight(s) dismissed.`,
        status: "success",
        isClosable: true,
      });
      await onRefetch();
    } catch (err: unknown) {
      toast({
        title: "Could not dismiss all",
        description: (err as Error)?.message ?? "Could not save as ignored.",
        status: "warning",
        isClosable: true,
      });
    } finally {
      setDismissAllLoading(false);
    }
  }, [pageIntents, toast, onRefetch]);

  if (totalCount === 0) {
    return (
      <VStack align="stretch" spacing={3} w="100%">
        <HStack justify="space-between" w="100%">
          <HStack gap={3}>
            <Text
              fontSize="24px"
              fontWeight="600"
              letterSpacing="-0.72px"
              color="gray.800"
            >
              Proactive Insights
            </Text>
            <Text fontSize="24px" fontWeight="600" color="blackAlpha.500">
              0
            </Text>
          </HStack>
        </HStack>
        <Box
          bg="white"
          borderRadius="16px"
          borderWidth="1px"
          borderColor="gray.200"
          p={6}
          shadow="sm"
        >
          <Text color="gray.500" fontSize="sm">
            Insights will appear here as campaigns run. Use the list below to
            manage campaigns.
          </Text>
        </Box>
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={3} w="100%">
      {/* Header: title + count | Sort + dropdown */}
      <HStack justify="space-between" w="100%" flexWrap="wrap" gap={3}>
        <HStack gap={3}>
          <Text
            fontSize="24px"
            fontWeight="600"
            letterSpacing="-0.72px"
            color="gray.800"
            lineHeight="1.2"
          >
            Proactive Insights
          </Text>
          <Text
            fontSize="24px"
            fontWeight="600"
            color="blackAlpha.500"
            lineHeight="1.2"
          >
            {totalCount}
          </Text>
        </HStack>
        <HStack gap={3} h="36px" align="center">
          <Text
            fontSize="xs"
            fontWeight="700"
            color="gray.500"
            letterSpacing="0.6px"
            textTransform="uppercase"
          >
            Sort:
          </Text>
          <Select
            width="160px"
            size="sm"
            bg="white"
            borderColor="gray.300"
            borderRadius="8px"
            shadow="0px 4px 12px 0px rgba(0,0,0,0.05)"
            fontSize="sm"
            fontWeight="500"
            color="gray.600"
          >
            <option>Most Urgent</option>
          </Select>
        </HStack>
      </HStack>

      {/* Insights table card */}
      <Box
        bg="white"
        borderRadius="16px"
        borderWidth="1px"
        borderColor="gray.200"
        overflow="hidden"
        shadow="sm"
      >
        <VStack
          spacing={0}
          align="stretch"
          divider={<Box borderTopWidth="1px" borderColor="gray.200" />}
        >
          {pageIntents.map((intent) => {
            const variant = getIntentVariant(intent);
            const isPositive = variant === "positive";
            const entityName = getEntityName(intent, campaignNames);
            const description = getIntentDescription(intent);
            const fullExplanation = getFullExplanation(intent);
            const actionLabel = getActionLabel(intent);
            const applicable =
              intent.actionable &&
              ((intent.status as string) === "SUGGESTED" ||
                (intent.status as string) === undefined);

            return (
              <Tooltip
                key={intent.id}
                label={fullExplanation}
                hasArrow
                placement="top"
                maxW="360px"
                whiteSpace="pre-wrap"
                isDisabled={!fullExplanation}
              >
                <Box
                  p={3}
                  w="100%"
                  display="grid"
                  gridTemplateColumns="minmax(0, 480px) 1fr auto"
                  columnGap={4}
                  alignItems="center"
                  _hover={{ bg: "gray.50" }}
                  cursor={fullExplanation ? "help" : undefined}
                >
                  <HStack minW={0} gap={3}>
                    <Box
                      w="26px"
                      h="26px"
                      borderRadius="6px"
                      bg={
                        isPositive
                          ? "green.100"
                          : variant === "negative"
                            ? "red.100"
                            : "gray.100"
                      }
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                    >
                      <Icon
                        as={isPositive ? FiTrendingUp : FiTrendingDown}
                        boxSize={4}
                        color={
                          isPositive
                            ? "green.700"
                            : variant === "negative"
                              ? "red.700"
                              : "gray.600"
                        }
                      />
                    </Box>
                    <Text
                      fontSize="md"
                      fontWeight="600"
                      color="gray.800"
                      noOfLines={1}
                      title={entityName}
                    >
                      {entityName}
                    </Text>
                  </HStack>
                  <Box minW={0} px={2} overflow="hidden">
                    <Text
                      fontSize="sm"
                      fontWeight="600"
                      color="gray.600"
                      noOfLines={2}
                      title={description}
                    >
                      {description}
                    </Text>
                  </Box>
                  <HStack flexShrink={0} justify="flex-end">
                    {applicable ? (
                      <Link
                        as="button"
                        type="button"
                        fontSize="sm"
                        fontWeight="500"
                        color="blue.600"
                        textDecoration="underline"
                        textDecorationStyle="dotted"
                        _hover={{ color: "blue.700" }}
                        onClick={() => {
                          if (applyingId !== intent.id) handleApply(intent);
                        }}
                        opacity={applyingId === intent.id ? 0.8 : 1}
                        pointerEvents={
                          applyingId === intent.id ? "none" : "auto"
                        }
                        bg="transparent"
                        border="none"
                        cursor="pointer"
                      >
                        {applyingId === intent.id ? "Applying…" : actionLabel}
                      </Link>
                    ) : (
                      <Text fontSize="sm" color="gray.500">
                        {actionLabel}
                      </Text>
                    )}
                  </HStack>
                </Box>
              </Tooltip>
            );
          })}
        </VStack>

        {/* Footer: Apply All | Dismiss all — Previous | Showing x-y of z | Next */}
        <HStack
          justify="space-between"
          p={3}
          borderTopWidth="1px"
          borderColor="gray.200"
          flexWrap="wrap"
          gap={3}
        >
          <HStack gap={4}>
            <Button
              size="sm"
              h="40px"
              px={3}
              borderRadius="8px"
              bg="gray.800"
              color="white"
              fontWeight="500"
              fontSize="sm"
              shadow="0px 4px 10px 0px rgba(0,0,0,0.1)"
              _hover={{ bg: "gray.700" }}
              onClick={handleApplyAll}
              isLoading={applyAllLoading}
              loadingText="Applying…"
              isDisabled={
                applyAllLoading ||
                pageIntents.filter((i) => i.actionable && i.campaign_id)
                  .length === 0
              }
            >
              Apply All
            </Button>
            <Button
              size="sm"
              variant="outline"
              h="40px"
              px={3}
              borderRadius="8px"
              borderColor="gray.300"
              fontWeight="500"
              fontSize="sm"
              color="gray.600"
              bg="white"
              shadow="0px 4px 10px 0px rgba(0,0,0,0.1)"
              _hover={{ bg: "gray.50" }}
              onClick={handleDismissAll}
              isLoading={dismissAllLoading}
              loadingText="Dismissing…"
              isDisabled={dismissAllLoading || pageIntents.length === 0}
            >
              Dismiss all
            </Button>
          </HStack>
          <HStack gap={3} align="center">
            <Button
              size="sm"
              variant="outline"
              fontSize="sm"
              fontWeight="400"
              color="gray.700"
              borderColor="gray.300"
              borderRadius="8px"
              isDisabled={page <= 1}
              opacity={page <= 1 ? 0.5 : 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Text fontSize="sm" color="gray.600">
              Showing{" "}
              <Text as="span" fontWeight="600" color="gray.800">
                {totalCount === 0 ? 0 : start + 1}-
                {Math.min(start + PAGE_SIZE, totalCount)}
              </Text>{" "}
              of{" "}
              <Text as="span" fontWeight="600" color="gray.800">
                {totalCount}
              </Text>
            </Text>
            <Button
              size="sm"
              variant="outline"
              fontSize="sm"
              fontWeight="400"
              color="gray.700"
              borderColor="gray.300"
              borderRadius="8px"
              isDisabled={page >= pageCount}
              onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            >
              Next
            </Button>
          </HStack>
        </HStack>
      </Box>
    </VStack>
  );
}
